import Sortable from 'devextreme-react/sortable';
import { useCallback, useMemo, useRef } from 'react';
import { useShellLayout } from '../shell/useShellLayout';
import { type PatientTrackingApi, patientTrackingApi } from './patientTrackingApi';
import { filterKanbanColumnEntries, type KanbanLayoutConfig } from './patientTrackingKanban';
import type { TrackerBoardLiveData } from './useLiveRows';
import {
  PATIENT_STATUS_LABELS,
  type PatientStatus,
  type TrackerEntryDto
} from './patientTrackingTypes';
import './patientTracking.css';

export interface PatientKanbanBoardProps {
  live: TrackerBoardLiveData;
  api?: PatientTrackingApi;
  layout: KanbanLayoutConfig;
  onLayoutChange: (layout: KanbanLayoutConfig) => void;
  onOpenView: (entry: TrackerEntryDto) => void;
}

function truncateText(value: string, maxLength = 120): string {
  const trimmed = value.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength - 1)}…`;
}

/** Drag-to-column Kanban with demo-style layout, color bars, and reorderable columns. */
export function PatientKanbanBoard({
  live,
  api = patientTrackingApi,
  layout,
  onLayoutChange,
  onOpenView
}: PatientKanbanBoardProps) {
  const { rows, loading, error, applyUpsert } = live;
  // On narrow/touch layouts, drag-and-drop is disabled so the board can be swiped/scrolled
  // horizontally between columns. Card status moves happen via the tap-open editor instead.
  const { isNarrow } = useShellLayout();
  const hidden = useMemo(() => new Set(layout.hidden), [layout.hidden]);
  const visibleColumns = useMemo(
    () => layout.order.filter((status) => !hidden.has(status)),
    [hidden, layout.order]
  );
  const suppressCardClickRef = useRef(false);

  const lists = useMemo(() => {
    const map = new Map<PatientStatus, TrackerEntryDto[]>();
    for (const status of visibleColumns) map.set(status, []);
    for (const row of rows) {
      if (!map.has(row.status)) continue;
      map.get(row.status)?.push(row);
    }
    for (const status of visibleColumns) {
      map.set(status, filterKanbanColumnEntries(map.get(status) ?? [], status));
    }
    return map;
  }, [rows, visibleColumns]);

  const persistColumnOrder = useCallback(
    (nextOrder: PatientStatus[]) => {
      onLayoutChange({ ...layout, order: nextOrder });
    },
    [layout, onLayoutChange]
  );

  const moveTo = async (entry: TrackerEntryDto, status: PatientStatus) => {
    if (entry.status === status) return;
    const optimistic = { ...entry, status };
    applyUpsert(optimistic);
    try {
      const dto = await api.updateTrackerEntry(entry.id, {
        pharmacyId: entry.pharmacyId,
        status,
        patientId: entry.patientId,
        coveredEntityId: entry.coveredEntityId,
        currentIssue: entry.currentIssue,
        enrollmentForm: null,
        changeLog: entry.changeLog,
        internalNote: entry.internalNote
      });
      applyUpsert(dto);
    } catch (e) {
      applyUpsert(entry);
      window.alert(e instanceof Error ? e.message : 'Could not move tracker entry.');
    }
  };

  if (loading && rows.length === 0) {
    return <div className="patient-kanban__loading">Loading…</div>;
  }

  if (visibleColumns.length === 0) {
    return (
      <div className="patient-kanban">
        <div className="patient-kanban__empty">
          All Kanban columns are hidden. Open column settings to show at least one column.
        </div>
      </div>
    );
  }

  const renderCard = (entry: TrackerEntryDto) => (
    <div
      className="patient-kanban__card"
      key={entry.id}
      role="button"
      tabIndex={0}
      onClick={() => {
        if (suppressCardClickRef.current) return;
        onOpenView(entry);
      }}
      onDoubleClick={() => onOpenView(entry)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpenView(entry);
        }
      }}
    >
      <div
        className={`patient-kanban__card-bar patient-kanban__card-bar--${entry.status}`}
        aria-hidden
      />
      <div className="patient-kanban__card-body">
        <div className="patient-kanban__card-subject">
          {truncateText(entry.currentIssue) || '(no issue description)'}
        </div>
        <div className="patient-kanban__card-assignee">
          {entry.patientName || '(unknown patient)'}
        </div>
        <div className="patient-kanban__card-meta">{entry.coveredEntityName ?? '—'}</div>
      </div>
    </div>
  );

  const renderColumn = (status: PatientStatus) => {
    const items = lists.get(status) ?? [];
    const cards = items.map(renderCard);
    return (
      <div className="patient-kanban__col" key={status}>
        <div className="patient-kanban__col-head">
          <span>{PATIENT_STATUS_LABELS[status]}</span>
          <span className="patient-kanban__col-count">{items.length}</span>
        </div>
        <div className="patient-kanban__list-scroll">
          {isNarrow ? (
            // Drag disabled on narrow so vertical/horizontal swipe scrolls natively;
            // status moves happen via the tap-open editor instead.
            <div className="patient-kanban__list">{cards}</div>
          ) : (
            <Sortable
              className="patient-kanban__list"
              group="tracker"
              data={status}
              onDragStart={() => {
                suppressCardClickRef.current = true;
              }}
              onDragEnd={() => {
                window.setTimeout(() => {
                  suppressCardClickRef.current = false;
                }, 0);
              }}
              onAdd={(e) => {
                const fromStatus = e.fromData as PatientStatus;
                const sourceItems = filterKanbanColumnEntries(
                  rows.filter((row) => row.status === fromStatus),
                  fromStatus
                );
                const moved = sourceItems[e.fromIndex];
                if (moved) void moveTo(moved, status);
              }}
            >
              {cards}
            </Sortable>
          )}
        </div>
      </div>
    );
  };

  const columns = visibleColumns.map(renderColumn);

  return (
    <div className="patient-kanban">
      {error ? <div className="patient-kanban__error">{error}</div> : null}
      <div className="patient-kanban__board-scroll">
        {isNarrow ? (
          <div className="patient-kanban__columns">{columns}</div>
        ) : (
          <Sortable
            className="patient-kanban__columns"
            itemOrientation="horizontal"
            handle=".patient-kanban__col-head"
            onReorder={(e) => {
              const visibleOrder = [...visibleColumns];
              const [moved] = visibleOrder.splice(e.fromIndex, 1);
              visibleOrder.splice(e.toIndex, 0, moved);
              let visibleIndex = 0;
              const nextOrder = layout.order.map((status) =>
                hidden.has(status) ? status : visibleOrder[visibleIndex++]
              );
              persistColumnOrder(nextOrder);
            }}
          >
            {columns}
          </Sortable>
        )}
      </div>
    </div>
  );
}
